"use client";
import React, { useState, useEffect, useCallback, memo } from "react";

interface CarouselProps {
  field?: any;
  carouselData?: any[];
  children: React.ReactNode;
}
const carouselTrackStyle = Object.freeze({
  overflow: "hidden",
  width: "100%",
});
const carouselDot = Object.freeze({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.5)",
  border: "none",
  cursor: "pointer",
  padding: "0px",
  transition: "all 300ms ease-in-out",
});

const carouselActiveDot = Object.freeze({
  backgroundColor: "white",
  width: "16px",
});
const Carousel = ({
  field,
  children,
  carouselData = []
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Calculate total number of possible positions
  const totalSlides = Math.max(
    0,
    carouselData.length -
      (field?.properties?.maxItemsPerView > 1
        ? field?.properties?.maxItemsPerView - 1
        : 0)
  );
  const moveToIndex = useCallback(
    (index: number) => {
      let newIndex = index;
      if (field?.properties?.gridLoop) {
        if (index < 0) {
          newIndex = totalSlides - 1;
        } else if (index >= totalSlides) {
          newIndex = 0;
        }
      } else {
        if (index < 0) {
          newIndex = 0;
        } else if (index >= totalSlides) {
          newIndex = totalSlides - 1;
        }
      }

      setCurrentIndex(newIndex);
    },
    [totalSlides, field?.properties?.gridLoop]
  );

  const next = useCallback(() => {
    moveToIndex(currentIndex + 1);
  }, [currentIndex, moveToIndex]);

  const prev = useCallback(() => {
    moveToIndex(currentIndex - 1);
  }, [currentIndex, moveToIndex]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      next();
    }
    if (isRightSwipe) {
      prev();
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (field?.properties?.loop) {
      interval = setInterval(() => {
        next();
      }, field?.properties?.autoplayInterval ?? 5000);
    }
    return () => clearInterval(interval);
  }, [field?.properties?.loop, field?.properties?.autoplayInterval, next]);

  // Calculate item width based on container and field?.properties?.gap
  const itemStyle: React.CSSProperties = {
    width:
      typeof field?.properties?.itemWidth === "number"
        ? `${field?.properties?.itemWidth}px`
        : field?.properties?.itemWidth,
    flexBasis: `calc((100% - ${
      field?.properties?.gap * (field?.properties?.maxItemsPerView - 1)
    }px) / ${field?.properties?.maxItemsPerView})`,
    marginRight: `${field?.properties?.gap}px`,
    gap: field?.properties?.gap.toString() + "px",

    ...field?.properties?.carouselItemStyle,
    display: "flex",
  };
  // const handleHorizontalScroll = (e: React.WheelEvent<HTMLDivElement>) => {
  //   e.preventDefault();
  //   const container = e.currentTarget;
  //   container.scrollLeft += e.deltaY;
  // };
  return (
    <>
      <div
        style={{
          // position: field?.properties?.mainContainerStyle?.position,
          // width: field?.properties?.mainContainerStyle?.width,
          ...field?.properties?.mainContainerStyle,
        }}
        // onMouseEnter={() => setIsHovered(true)}
        // onMouseLeave={() => setIsHovered(false)}
      >
        <div
          className={`${field?.properties?.carouseTrackClassName ?? ""}`}
          // onWheel={handleHorizontalScroll}
          style={{
            ...field?.properties?.backgroundColour,
            ...(!field?.properties?.carouseTrackClassName
              ? carouselTrackStyle
              : {}),
          }}
        >
          <div
            style={{
              transform: `translateX(calc(-${
                Math.ceil(currentIndex * Math.ceil(100 / field?.properties?.maxItemsPerView))
              }%  ${
                field?.properties?.maxItemsPerView > 1 ? ( " + "+currentIndex + "px"):" - "+
                (((currentIndex && field?.properties?.maxItemsPerView === 1?((field?.properties?.gap * currentIndex)):"0")+"px ") +" + " +
                (field?.properties?.maxItemsPerView<currentIndex && field?.properties?.maxItemsPerView>1?
                  (field?.properties?.maxItemsPerView * currentIndex)+field?.properties?.gap:
                  field?.properties?.maxItemsPerView>1 && currentIndex?field?.properties?.maxItemsPerView:0)+"px")
                }
             
              ))`,
              ...field?.properties?.carouselitemsContainerStyle,
              position: "sticky",
            }}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <div style={itemStyle}>
              {children}
            </div>
          </div>
        </div>

        {field?.properties?.showButtons && totalSlides > 1 && (
          <>
            {(currentIndex > 0 || field?.properties?.showButtonsDefault) && (
              <button
                onClick={prev}
                name="carouselPrevious"
                aria-label="carouselPrevious"
                style={{
                  display:
                    !field?.properties?.gridLoop && currentIndex === 0
                      ? "none"
                      : "block",

                  ...field?.properties?.arrowsContainerStyle,
                  ...field?.properties?.prevArrowStyle,
                }}
                className={!field?.properties?.hoverEffect ? "carousel-hover-button" : ""}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
          {(currentIndex !=(parseInt(String(totalSlides)))-1 || field?.properties?.showButtonsDefault) &&  <button
              onClick={next}
              name="carouselNext"
              aria-label="carouselNext"
              style={{
                display:
                  !field?.properties?.gridLoop &&
                  currentIndex === totalSlides - 1
                    ? "none"
                    : "block",

                ...field?.properties?.arrowsContainerStyle,
                ...field?.properties?.nextArrowStyle,
              }}
              className={!field?.properties?.hoverEffect ? "carousel-hover-button" : ""}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>}
          </>
        )}

        {field?.properties?.showDots && (
          <div
            style={{
              ...field?.properties?.dotContainerStyle,
            }}
          >
            {Array.from({ length: totalSlides }).map((_, index) => (
              <span
                key={index}
                onClick={() => moveToIndex(index)}
                className={""}
                style={{
                  ...carouselDot,
                  ...field?.properties?.dotStyle,
                  ...(currentIndex === index ? carouselActiveDot : {}),
                  width:
                    currentIndex === index
                      ? field?.properties?.dotStyleActive?.width
                      : field?.properties?.dotStyle?.width,

                  backgroundColor:
                    currentIndex === index
                      ? field?.properties?.dotStyleActive?.backgroundColor
                      : field?.properties?.dotStyle?.backgroundColor,
                }}
              ></span>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default memo(Carousel);
